import { useTranslation } from "react-i18next";

import { useNavigationMenu, type MenuTreeNode } from "src/api/client-api/menu/useNavigationMenu";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { resolveMenuIcon } from "src/utils/menuIcon";
import type { IngredientPickerSource } from "./useIngredientPickerRows";

const findIngredientMenuItem = (
    nodes: MenuTreeNode[],
    source: IngredientPickerSource,
): MenuTreeNode["item"] | undefined => {
    const intermediate = source === "intermediate-product";
    const translationKey = intermediate ? "sidebar.intermediate_products" : "menu.products.ingredients";
    const pathSuffix = intermediate ? "/intermediate-products" : "/ingredients";
    for (const node of nodes) {
        const { item } = node;
        const path = item.url?.split(/[?#]/, 1)[0].replace(/\/$/, "");

        if (item.i18nKey === translationKey || path?.endsWith(pathSuffix)) {
            return item;
        }

        const nestedItem = findIngredientMenuItem(node.children, source);
        if (nestedItem !== undefined) return nestedItem;
    }

    return undefined;
};

export const useIngredientTypeDisplay = () => {
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();
    const { menuTree } = useNavigationMenu(selectedCompany?.id);
    const ingredient = findIngredientMenuItem(menuTree, "raw-material");
    const intermediateProduct = findIngredientMenuItem(menuTree, "intermediate-product");

    return {
        "raw-material": {
            singularLabel: t("entity.ingredient", "Ingredient"),
            label:
                ingredient ?
                    t(ingredient.i18nKey || "", ingredient.label)
                :   t("menu.products.ingredients", "Ingredients"),
            icon: ingredient ? resolveMenuIcon(ingredient.icon) : ("wheat" as const),
        },
        "intermediate-product": {
            singularLabel: t("intermediate_product", "Intermediate product"),
            label:
                intermediateProduct ?
                    t(intermediateProduct.i18nKey || "", intermediateProduct.label)
                :   t("sidebar.intermediate_products", "Intermediate products"),
            icon: intermediateProduct ? resolveMenuIcon(intermediateProduct.icon) : ("flour" as const),
        },
    };
};
